import type {CmLogger, LoggerDomain, LogLevel} from "@cm/log"
import {LOG_LEVELS} from "@cm/log"

/**
 * Browser-based logger that wraps console methods with level filtering.
 * Log level is determined dynamically via the getLevel callback.
 */
export class BrowserLogger implements CmLogger {
    constructor(
        private readonly domain: LoggerDomain,
        private readonly getLevel: () => LogLevel,
    ) {}

    private shouldLog(level: LogLevel): boolean {
        return LOG_LEVELS[level] <= LOG_LEVELS[this.getLevel()]
    }

    private formatPrefix(): string {
        return `[${this.domain}]`
    }

    isEnabled(level: LogLevel): boolean {
        return this.shouldLog(level)
    }

    error(message: string, ...data: unknown[]): void {
        if (this.shouldLog("error")) {
            console.error(this.formatPrefix(), message, ...data) // eslint-disable-line no-console
        }
    }

    warn(message: string, ...data: unknown[]): void {
        if (this.shouldLog("warn")) {
            console.warn(this.formatPrefix(), message, ...data) // eslint-disable-line no-console
        }
    }

    info(message: string, ...data: unknown[]): void {
        if (this.shouldLog("info")) {
            console.info(this.formatPrefix(), message, ...data) // eslint-disable-line no-console
        }
    }

    log(message: string, ...data: unknown[]): void {
        if (this.shouldLog("log")) {
            console.log(this.formatPrefix(), message, ...data) // eslint-disable-line no-console
        }
    }

    debug(message: string, ...data: unknown[]): void {
        if (this.shouldLog("debug")) {
            console.debug(this.formatPrefix(), message, ...data) // eslint-disable-line no-console
        }
    }

    trace(message: string, ...data: unknown[]): void {
        if (this.shouldLog("trace")) {
            console.debug(this.formatPrefix(), message, ...data) // eslint-disable-line no-console
        }
    }

    errorLazy(messageFn: () => string, ...data: unknown[]): void {
        if (this.shouldLog("error")) {
            console.error(this.formatPrefix(), messageFn(), ...data) // eslint-disable-line no-console
        }
    }

    warnLazy(messageFn: () => string, ...data: unknown[]): void {
        if (this.shouldLog("warn")) {
            console.warn(this.formatPrefix(), messageFn(), ...data) // eslint-disable-line no-console
        }
    }

    infoLazy(messageFn: () => string, ...data: unknown[]): void {
        if (this.shouldLog("info")) {
            console.info(this.formatPrefix(), messageFn(), ...data) // eslint-disable-line no-console
        }
    }

    logLazy(messageFn: () => string, ...data: unknown[]): void {
        if (this.shouldLog("log")) {
            console.log(this.formatPrefix(), messageFn(), ...data) // eslint-disable-line no-console
        }
    }

    debugLazy(messageFn: () => string, ...data: unknown[]): void {
        if (this.shouldLog("debug")) {
            console.debug(this.formatPrefix(), messageFn(), ...data) // eslint-disable-line no-console
        }
    }

    traceLazy(messageFn: () => string, ...data: unknown[]): void {
        if (this.shouldLog("trace")) {
            console.debug(this.formatPrefix(), messageFn(), ...data) // eslint-disable-line no-console
        }
    }
}

/**
 * Create a browser logger for a specific domain.
 *
 * @param domain - The logger domain (e.g., "api", "auth", "rendering")
 * @param getLevel - Callback that returns the current log level for this domain
 */
export function createBrowserLogger(domain: LoggerDomain, getLevel: () => LogLevel = () => "debug"): CmLogger {
    return new BrowserLogger(domain, getLevel)
}
